import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import { ShieldCheck, ChevronRight, ChevronLeft, ExternalLink } from 'lucide-react';
import UpgradePrompt from '../Subscriptions/UpgradePrompt';
import { isFreeSubscription, readUserFromStorage } from '../../utils/subscription';
import { showWarning } from '../../utils/toast';
import '../../App.css';

const STEP_KEYS = ['intro', 'insuranceRealities', 'cashBuffer', 'brokerExpectations', 'complianceBasics', 'summary'];
const TOTAL_STEPS = STEP_KEYS.length;

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: token ? `Bearer ${token}` : '' } };
}

export default function AuthorityCopilotPage() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [user, setUser] = useState(() => readUserFromStorage());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [completing, setCompleting] = useState(false);

  const currentStep = progress?.currentStep ?? 0;
  const answers = progress?.readinessAnswers ?? {};
  const isFree = isFreeSubscription(user);
  const planBlocked = isFree;

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    if (!planBlocked) return;
    showWarning(
      'Authority Setup Copilot is available on Owner-Operator and Fleet/Pro plans. Upgrade to unlock.',
    );
    navigate('/authority', { replace: true });
  }, [planBlocked, navigate]);

  useEffect(() => {
    if (planBlocked) return;
    const fetch = async () => {
      try {
        const [contentRes, progressRes] = await Promise.all([
          axios.get(`${API_URL}/api/authority-copilot/content`),
          axios.get(`${API_URL}/api/authority-copilot/progress`, getAuthHeaders()).catch(() => ({ data: null })),
        ]);
        setContent(contentRes.data);
        setProgress(progressRes.data || { currentStep: 0, readinessAnswers: {} });
      } catch (err) {
        console.error('Copilot load error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [planBlocked]);

  const saveStep = async (nextStep, answerUpdates = {}) => {
    if (!progress) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/authority-copilot/progress`,
        {
          currentStep: nextStep,
          readinessAnswers: answerUpdates,
        },
        getAuthHeaders(),
      );
      setProgress(res.data);
    } catch (err) {
      console.error('Save progress error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (currentStep >= TOTAL_STEPS - 1) {
      if (isFree) {
        setShowPaywall(true);
        return;
      }
      handleComplete();
      return;
    }
    saveStep(currentStep + 1);
  };

  const handleComplete = async () => {
    if (!disclaimerAccepted) return;
    setCompleting(true);
    try {
      await axios.post(
        `${API_URL}/api/authority-copilot/complete`,
        { acceptDisclaimer: true },
        getAuthHeaders(),
      );
      navigate('/authority-copilot/plan');
    } catch (err) {
      if (err.response?.status === 403) {
        setShowPaywall(true);
      }
      console.error('Complete copilot error:', err);
    } finally {
      setCompleting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) saveStep(currentStep - 1);
  };

  const setSectionAnswers = (sectionKey, itemId, value) => {
    const next = {
      ...answers,
      [sectionKey]: { ...(answers[sectionKey] || {}), [itemId]: value },
    };
    setProgress((p) => (p ? { ...p, readinessAnswers: next } : p));
    saveStep(currentStep, next);
  };

  if (planBlocked) {
    return null;
  }

  if (loading || !content) {
    return (
      <div className="copilot-page">
        <div className="copilot-loading">Loading Authority Setup Copilot...</div>
      </div>
    );
  }

  const readiness = content.readiness || {};
  const stepKey = STEP_KEYS[currentStep];

  return (
    <div className="copilot-page">
      <div className="copilot-wizard card">
        <div className="copilot-header">
          <ShieldCheck size={28} color="var(--primary)" />
          <h1>Authority Setup Copilot</h1>
          <p className="copilot-tagline">Education & readiness — not a filing service. Are you ready?</p>
        </div>

        <div className="copilot-progress-bar">
          <div
            className="copilot-progress-fill"
            style={{ width: `${((currentStep + 1) / TOTAL_STEPS) * 100}%` }}
          />
        </div>
        <p className="copilot-step-label">Step {currentStep + 1} of {TOTAL_STEPS}</p>

        {stepKey === 'intro' && (
          <div className="copilot-step copilot-intro">
            <p className="copilot-disclaimer-teaser">{content.disclaimer}</p>
            <p>
              This checklist walks you through insurance realities, cash buffer planning, onboarding expectations, and compliance basics.
              Official FMCSA links are provided for reference. Completing the steps gives you a clearer setup plan and stronger operational visibility.
            </p>
            <ul className="copilot-intro-list">
              <li>Review readiness topics and check off what you understand</li>
              <li>Get a personalized setup plan and required filings checklist</li>
              <li>See a timeline and cost estimator</li>
              <li>Get recommended next actions inside Sovrynn</li>
            </ul>
            <p className="copilot-intro-cta">No legal advice — for education only. Ready to start?</p>
          </div>
        )}

        {stepKey !== 'intro' && stepKey !== 'summary' && readiness[stepKey] && (
          <div className="copilot-step copilot-readiness">
            <h2>{readiness[stepKey].title}</h2>
            <p className="copilot-section-intro">{readiness[stepKey].intro}</p>
            <p className="copilot-disclaimer-small">{readiness[stepKey].disclaimer}</p>
            <ul className="copilot-checklist">
              {(readiness[stepKey].items || []).map((item) => (
                <li key={item.id} className="copilot-checklist-item">
                  <label className="copilot-check-label">
                    <input
                      type="checkbox"
                      checked={!!(answers[stepKey] && answers[stepKey][item.id])}
                      onChange={(e) => setSectionAnswers(stepKey, item.id, e.target.checked)}
                    />
                    <span>{item.label}</span>
                  </label>
                  <p className="copilot-item-desc">{item.description}</p>
                  {item.linkUrl && (
                    <a
                      href={item.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="copilot-resource-link"
                    >
                      {item.linkLabel || 'Official resource'} <ExternalLink size={14} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {stepKey === 'summary' && (
          <div className="copilot-step copilot-summary">
            <h2>Are you ready?</h2>
            <p>You’ve reviewed the readiness topics. Your personalized plan, required filings checklist, timeline, and cost estimator are ready.</p>
            <p className="copilot-disclaimer-small">{content.disclaimer}</p>
            <label className="copilot-disclaimer-accept">
              <input
                type="checkbox"
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
              />
              <span>I understand this is for education only and not legal or regulatory advice.</span>
            </label>
            <p className="copilot-unlock-hint">
              {isFree
                ? 'Unlock your full plan with a paid plan to save your personalized setup plan, filings checklist, timeline, and next actions.'
                : 'Generate and save your plan below.'}
            </p>
          </div>
        )}

        <div className="copilot-wizard-actions">
          {currentStep > 0 && (
            <button type="button" className="button secondary" onClick={handleBack} disabled={saving}>
              <ChevronLeft size={18} /> Back
            </button>
          )}
          <div className="copilot-wizard-next">
            {stepKey === 'summary' ? (
              <button
                type="button"
                className="button primary"
                onClick={handleNext}
                disabled={!disclaimerAccepted || completing}
              >
                {isFree ? 'Unlock my plan' : 'Generate & save my plan'}
              </button>
            ) : (
              <button type="button" className="button primary" onClick={handleNext} disabled={saving}>
                Next <ChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {showPaywall && (
        <UpgradePrompt
          feature="your Authority Setup Plan (personalized plan, filings checklist, timeline & cost estimator)"
          onClose={() => setShowPaywall(false)}
        />
      )}
    </div>
  );
}
